import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Radio, ExternalLink, ScanSearch, Filter } from "lucide-react";
import { getRecentBills, lookupBill } from "@/lib/api";

const PREFIX_FILTERS = [
  { code: "ALL", full: "" },
  { code: "AB", full: "Assembly Bill" },
  { code: "SB", full: "Senate Bill" },
  { code: "ACA", full: "Assembly Constitutional Amendment" },
  { code: "SCA", full: "Senate Constitutional Amendment" },
  { code: "ACR", full: "Assembly Concurrent Resolution" },
  { code: "SCR", full: "Senate Concurrent Resolution" },
  { code: "AJR", full: "Assembly Joint Resolution" },
  { code: "SJR", full: "Senate Joint Resolution" },
];

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 0) return "just now";
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return "";
  }
}

export default function RecentBillsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [openingLabel, setOpeningLabel] = useState(null);

  useEffect(() => {
    let alive = true;
    getRecentBills({ limit: 200 })
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((err) => {
        if (alive) toast.error(err?.response?.data?.detail || "Could not load recent bills.");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const all = data?.discoveries || [];
    if (filter === "ALL") return all;
    return all.filter((d) => d.prefix === filter);
  }, [data, filter]);

  const handleOpenBill = async (label) => {
    setOpeningLabel(label);
    try {
      const r = await lookupBill({ query: label });
      navigate(`/measures/${r.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not open this bill.";
      toast.error(msg);
      setOpeningLabel(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="recent-bills-page">
      <header className="mb-8 pb-5 border-b border-[hsl(var(--rule))]">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="stamp stamp-explicit">Live</span>
          <span className="stamp stamp-implied">CA Legislature</span>
          {data?.last_scan?.finished_at && (
            <span className="text-[11px] font-mono text-[hsl(var(--ink-muted))]">
              · last scan {timeAgo(data.last_scan.finished_at)} · {data.last_scan.total_found} new
            </span>
          )}
        </div>
        <h1 className="hero-title text-3xl sm:text-4xl lg:text-5xl mb-3">
          Newly introduced California bills
        </h1>
        <p className="text-base text-[hsl(var(--ink-2))] max-w-2xl leading-relaxed">
          A live feed of bills as they appear on the official Legislature website.
          Click any bill to run the full Measure X-Ray analysis &mdash; plain English,
          current-law comparison, hidden provisions, who-pays / who-gains, and citations.
          Analysis is queued on demand so we don&rsquo;t burn AI budget on bills nobody
          reads.
        </p>
      </header>

      <section
        className="paper-card rounded-sm p-3 sm:p-4 mb-2 flex items-center gap-2 flex-wrap"
        data-testid="recent-bills-filters"
      >
        <Filter className="w-3.5 h-3.5 text-[hsl(var(--ink-muted))]" />
        <span className="section-label">Filter</span>
        {PREFIX_FILTERS.map(({ code, full }) => (
          <button
            key={code}
            type="button"
            onClick={() => setFilter(code)}
            data-testid={`recent-bills-filter-${code.toLowerCase()}`}
            title={full || "Show all bill types"}
            className={`px-2.5 py-1 rounded-sm text-xs font-mono uppercase tracking-wider transition-colors ${
              filter === code
                ? "bg-[hsl(var(--ink))] text-[hsl(var(--paper))]"
                : "border border-[hsl(var(--rule))] text-[hsl(var(--ink-2))] hover:border-[hsl(var(--ink))]"
            }`}
          >
            <span>{code}</span>
            {full && (
              <span className="hidden lg:inline normal-case font-sans tracking-normal text-[hsl(var(--ink-muted-on-dark)/_inherit)] opacity-80 ml-1.5">
                — {full}
              </span>
            )}
          </button>
        ))}
      </section>

      {/* Compact bill-code legend — visible on small screens, hidden when full labels show inline */}
      <div
        className="mb-5 text-[11px] font-mono text-[hsl(var(--ink-muted))] leading-relaxed lg:hidden"
        data-testid="bill-code-legend"
      >
        <span className="uppercase tracking-wider mr-2">Code guide:</span>
        AB&nbsp;= Assembly Bill · SB&nbsp;= Senate Bill · ACA/SCA&nbsp;=
        Constitutional Amendments · ACR/SCR&nbsp;= Concurrent Resolutions ·
        AJR/SJR&nbsp;= Joint Resolutions
      </div>

      {loading ? (
        <div className="paper-card rounded-sm p-10 text-center" data-testid="recent-bills-loading">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-[hsl(var(--accent-blue))]" />
          <div className="text-sm text-[hsl(var(--ink-muted))]">Loading recent bills…</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="paper-card rounded-sm p-10 text-center" data-testid="recent-bills-empty">
          <Radio className="w-6 h-6 mx-auto mb-2 text-[hsl(var(--ink-muted))]" />
          <div className="text-sm text-[hsl(var(--ink-2))] mb-1">No bills discovered yet.</div>
          <div className="text-xs text-[hsl(var(--ink-muted))]">
            The watcher runs on demand. Trigger a scan from the admin dashboard to populate this list.
          </div>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="recent-bills-list">
          {filtered.map((d) => {
            const isOpening = openingLabel === d.label;
            return (
              <li
                key={d.id}
                data-testid={`recent-bill-${d.id}`}
                className="paper-card rounded-sm p-4 flex items-start justify-between gap-3 hover:border-[hsl(var(--accent-blue))] transition-colors"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <span className="font-mono text-sm font-bold text-[hsl(var(--ink))]">
                      {d.label}
                    </span>
                    <span className="stamp stamp-implied">{d.prefix}</span>
                    {d.introduced_date && (
                      <span className="text-[10px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))]">
                        introduced {d.introduced_date}
                      </span>
                    )}
                    <span className="text-[10px] font-mono text-[hsl(var(--ink-muted))]">
                      · seen {timeAgo(d.discovered_at)}
                    </span>
                  </div>
                  {d.title && (
                    <div className="font-serif text-base sm:text-lg leading-snug mb-1">
                      {d.title}
                    </div>
                  )}
                  {d.subject && d.subject !== d.title && (
                    <div className="text-xs text-[hsl(var(--ink-muted))] mb-1">{d.subject}</div>
                  )}
                  {d.lead_author_block && (
                    <div className="text-xs text-[hsl(var(--ink-2))] mb-1">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))] mr-1.5">
                        Lead
                      </span>
                      {d.lead_author_block}
                    </div>
                  )}
                  {d.last_action && (
                    <div className="text-xs text-[hsl(var(--ink-muted))] italic">
                      {d.last_action}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleOpenBill(d.label)}
                    disabled={isOpening || !!openingLabel}
                    className="pill-btn pill-btn-primary disabled:opacity-60"
                    data-testid={`recent-bill-analyze-${d.id}`}
                  >
                    {isOpening ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <ScanSearch className="w-3.5 h-3.5" />
                    )}
                    {isOpening ? "Opening…" : "Analyze"}
                  </button>
                  {d.status_url && (
                    <a
                      href={d.status_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[10px] font-mono uppercase tracking-wider text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink))] inline-flex items-center gap-1"
                    >
                      leginfo <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-8 text-[11px] font-mono text-[hsl(var(--ink-muted))] text-center">
        Source: leginfo.legislature.ca.gov · Discovery is metadata-only.
        Full analysis is rate-limited per IP to keep this free for everyone.
      </div>
    </div>
  );
}
